import React, { ReactElement, useContext } from 'react';
import { Description, DescriptionPill, Option } from '../DropdownEditor/CustomOptions';
import { Stack } from '@fluentui/react';
import Fieldset from '../connectedForm/Fieldset';
import Price from '../connectedForm/Price';
import styled from 'styled-components';
import isFunction from '../../components/react-data-grid/utils/isFunction';
import { IDropdownOptionRenderer } from '../../@types/entityTypes';
import { IPricePotential } from '../../graphql/schema/p4types';
import SalesOrderContext from '../Context/SalesOrderContext';

const PriceRow = styled.div`
  box-sizing: border-box;
  display: flex;
  flex: 0 1 auto;
  flex-direction: row;
  flex-wrap: wrap;

  @media (max-width: 960px) {
    flex-direction: column;
  }
`;

export const PriceOption: IDropdownOptionRenderer = ({ option }) => {
  if (!option) return null;

  let breakQty = null;
  if (option.priceBreak) {
    breakQty = <DescriptionPill> {option.priceBreak}+</DescriptionPill>;
  }

  const definedTerms = [option.termA, option.termB, option.termC, option.termD, option.termE].filter(
    (term) => term > 0 || term < 0
  );

  const terms =
    definedTerms.length > 0 ? (
      <>
        Terms: <b>{definedTerms.join(', ')}</b>
      </>
    ) : null;

  let precision = option.decimals || option.precision || 2;
  if (isFunction(precision)) precision = precision(option);

  const nett = option.nettPrice ? (
    <>
      Nett:{' '}
      <b>
        <Price decimals={precision}>{option.nettPrice}</Price>
      </b>
    </>
  ) : null;

  let description = option.description;

  if (description && option?.priceOriginDescription) {
    description += ` (${option?.priceOriginDescription})`;
  } else if (!description) {
    description = 'MANUAL';
  }

  return (
    <Stack>
      <Option>
        <DescriptionPill>{description}</DescriptionPill>
        <Description>{breakQty}</Description>
      </Option>
      <PriceRow>
        <Fieldset cols={1 / 3}>
          Gross:{' '}
          <b>
            <Price decimals={precision}>{option.grossPrice || option.value || 0}</Price>
          </b>
        </Fieldset>
        <Fieldset cols={1 / 3}>{terms}</Fieldset>
        <Fieldset cols={1 / 3}>{nett}</Fieldset>
      </PriceRow>
    </Stack>
  );
};

export const NettOnlyPriceOption: IDropdownOptionRenderer = ({ option }: { option: IPricePotential }) => {
  const { vatInclusive } = useContext(SalesOrderContext);
  if (!option) return null;

  let breakQty = null;
  if (option.priceBreak) {
    breakQty = <DescriptionPill> {option.priceBreak}+</DescriptionPill>;
  }

  const nett = option.nettPrice ? (
    <b>
      <Price decimals={(option as any).decimals || option.precision || 2}>{option.nettPrice}</Price>
    </b>
  ) : null;

  //Display the alt price (Both excl/incl VAT are displayed - The primary one for the Customer on the left, and alternate on the right).
  let alternate: ReactElement | null;
  if (vatInclusive) {
    alternate = option.nettPrice ? (
      <Price decimals={(option as any).decimals || option.precision || 2} suffix={' Exc. VAT'}>
        {option.nettPriceExcludingVat}
      </Price>
    ) : null;
  } else {
    alternate = option.nettPrice ? (
      <Price decimals={(option as any).decimals || option.precision || 2} suffix={' Inc. VAT'}>
        {option.nettPriceIncludingVat}
      </Price>
    ) : null;
  }

  let description = option.description;

  if (description && option?.priceOriginDescription) {
    description += ` (${option?.priceOriginDescription})`;
  } else if (!description) {
    description = 'MANUAL';
  }

  return (
    <Stack>
      <Option>
        <DescriptionPill>{description}</DescriptionPill>
        <Description>{breakQty}</Description>
      </Option>
      <PriceRow>
        <Fieldset cols={1 / 2}>{nett}</Fieldset>
        <Fieldset cols={1 / 2}>{alternate}</Fieldset>
      </PriceRow>
    </Stack>
  );
};
